import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccessType, LocationType, ParkingType, Station, StationHours } from '@app/services/station.interface';
import { StationService } from '@app/services/station.service';
import { AuthService } from '@app/services/auth.service';
import { AddMissingChargerHoursDialogComponent } from '../add-missing-charger-hours-dialog/add-missing-charger-hours-dialog.component';
import { daysOfWeek, WEEKDAYS } from '@app/shared/utils/consts';
import { formatTime } from '@app/shared/utils';
import { EditChargerConnectorsDialogComponent } from '../edit-charger-connectors-dialog/edit-charger-connectors-dialog.component';

@Component({
  selector: 'app-edit-charger-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDividerModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  templateUrl: './edit-charger-dialog.component.html',
  styleUrl: './edit-charger-dialog.component.scss'
})
export class EditChargerDialogComponent implements OnInit {
  dialogRef = inject(MatDialogRef<EditChargerDialogComponent>);
  data: { station: Station } = inject(MAT_DIALOG_DATA);
  @ViewChild('dialogContainer') dialogContainer!: ElementRef;

  hours: StationHours;
  daysOfWeek = daysOfWeek;
  WEEKDAYS = WEEKDAYS;
  formatTime = formatTime;
  state = signal<'initial' | 'loading' | 'success' | 'error'>('initial');

  locationTypes: LocationType[] = [];
  accessTypes: AccessType[] = ['PUBLIC', 'PRIVATE', 'GUEST'];
  parkingTypes: ParkingType[] = ['FREE', 'PAID'];

  private _formBuilder = inject(FormBuilder);
  formGroup = this._formBuilder.group({
    name: ['', Validators.required],
    description: [''],
    locationTypeId: [''],
    phone: [''],
    website: [''],
    email: [''],
    accessType: [''],
    parkingType: [''],
  });

  constructor(
    private dialog: MatDialog,
    private stationService: StationService,
    private authService: AuthService,
  ) {}

  get userId(): string {
    return this.authService.userSignal()?.uid || '';
  }

  ngOnInit(): void {
    // Populate form with existing station data
    const station = this.data.station;
    this.formGroup.patchValue({
      name: station.name || '',
      description: station.description || '',
      locationTypeId: station.locationType?.id?.toString() || '',
      phone: station.phone || '',
      website: station.website || '',
      email: station.email || '',
      accessType: station.accessType || '',
      parkingType: station.parkingType || '',
    });
    // Set hours from station
    if (station.hours) {
      this.hours = { ...station.hours };
    }

    // Fetch location types
    this.stationService.getLocationTypes().subscribe(types => {
      this.locationTypes = types;
    });
  }

  close() {
    this.dialogRef.close();
  }

  openHoursDialog() {
    const dialogRef = this.dialog.open(AddMissingChargerHoursDialogComponent, {
      maxWidth: '500px',
      width: '500px',
      height: '845px',
      data: { hours: this.hours },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.hours) {
        this.hours = result?.hours;
      }
    });
  }

  openConnectorsDialog() {
    const dialogRef = this.dialog.open(EditChargerConnectorsDialogComponent, {
      maxWidth: '560px',
      width: '560px',
      maxHeight: '90vh',
      data: { station: this.data.station },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated) {
        // Refresh station data if connectors were updated
        this.data.station.chargers = result.chargers;
      }
    });
  }

  submitForm() {
    if (this.formGroup.invalid) return;

    this.state.set('loading');
    const stationId = this.data.station.id;
    const payload = {
      id: stationId,
      name: this.formGroup.value.name,
      featureGeometry: {
        type: 'Feature',
        geometry: this.data.station.location,
      },
      description: this.formGroup.value.description || undefined,
      phone: this.formGroup.value.phone || undefined,
      website: this.formGroup.value.website || undefined,
      email: this.formGroup.value.email || undefined,
      accessType: this.formGroup.value.accessType || undefined,
      parkingType: this.formGroup.value.parkingType || undefined,
      locationTypeId: this.formGroup.value.locationTypeId ? parseInt(this.formGroup.value.locationTypeId) : undefined,
    };
    this.stationService.createStation(payload as any).subscribe({
      next: () => {
        if (this.hours) {
          this.stationService.addHours(stationId, this.hours).subscribe({
            next: () => {
              this.state.set('success');
              this.scrollToTop();
              setTimeout(() => {
                this.dialogRef.close({ updated: true });
              }, 1500);
            },
            error: () => {
              this.state.set('error');
              this.scrollToTop();
            }
          });
        } else {
          this.state.set('success');
          this.scrollToTop();
          setTimeout(() => {
            this.dialogRef.close({ updated: true });
          }, 1500);
        }
      },
      error: () => {
        this.state.set('error');
        this.scrollToTop();
      },
    });
  }

  scrollToTop() {
    this.dialogContainer?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getConnectorsCount(): number {
    return this.data.station?.chargers?.reduce((acc, charger) => acc + (charger.connectors?.length || 0), 0) || 0;
  }
}
